import { useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Bell, Home, LogOut, Moon, Sun, User } from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { useAuthStore } from "@/store/authStore";
import { useThemeStore } from "@/store/themeStore";
import { groupNavItems, sectionLabel, rolePaths, type SidebarItem } from "./nav";

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: SidebarItem[];
}

/** Ctrl/⌘ K jump-to menu over the portal's navigation plus a few account actions. */
export default function CommandPalette({ open, onOpenChange, items }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const mode = useThemeStore((s) => s.mode);
  const toggleTheme = useThemeStore((s) => s.toggleTheme);
  const paths = rolePaths(user?.role);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onOpenChange]);

  const run = (fn: () => void) => {
    onOpenChange(false);
    fn();
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} title="Search pages and actions">
      <CommandInput placeholder="Search pages and actions…" />
      <CommandList>
        <CommandEmpty>No matches.</CommandEmpty>
        {Object.entries(groupNavItems(items)).map(([section, sectionItems]) => (
          <CommandGroup key={section} heading={sectionLabel(section)}>
            {sectionItems.map((item) => (
              <CommandItem
                key={item.path}
                value={`${item.label} ${section}`}
                onSelect={() => run(() => navigate({ to: item.path as any }))}
              >
                <span className="flex text-muted-foreground [&_svg]:size-4">{item.icon}</span>
                {item.label}
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
        <CommandSeparator />
        <CommandGroup heading="Account">
          <CommandItem onSelect={() => run(() => navigate({ to: paths.profile as any }))}>
            <User className="text-muted-foreground" />
            {user?.role === "admin" ? "Settings" : "My profile"}
          </CommandItem>
          <CommandItem onSelect={() => run(() => navigate({ to: paths.notifications as any }))}>
            <Bell className="text-muted-foreground" />
            Notifications
          </CommandItem>
          <CommandItem onSelect={() => run(toggleTheme)}>
            {mode === "dark" ? (
              <Sun className="text-muted-foreground" />
            ) : (
              <Moon className="text-muted-foreground" />
            )}
            Switch to {mode === "dark" ? "light" : "dark"} theme
          </CommandItem>
          <CommandItem onSelect={() => run(() => navigate({ to: "/" }))}>
            <Home className="text-muted-foreground" />
            Home page
          </CommandItem>
          <CommandItem
            onSelect={() =>
              run(() => {
                logout();
                navigate({ to: "/auth/login" });
              })
            }
          >
            <LogOut className="text-muted-foreground" />
            Sign out
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
